import { useState, useEffect } from "react";
import "./css/adminnegorequest.css";
import {
  getNegoRequests,
  getNegoRequestLogs,
  searchNegoRequests,
  searchNegoRequestLogs,
} from "../../utils/adminNegoRequestService";
import { formatDate } from "../../utils/commonService";
import Pagination from "../common/Pagination";

const STATUS_LABEL = {
  APPROVED: { label: "승인", className: "adminnegorequest-status-accepted" },
  REJECTED: { label: "거절", className: "adminnegorequest-status-rejected" },
  CANCELED: { label: "취소", className: "adminnegorequest-status-expired" },
};

const defaultFilters = {
  startDate: "",
  endDate: "",
  requestStatus: "",
  requestLogStatus: "",
  searchType: "productNm",
  searchTerm: "",
};

const AdminNegoRequest = () => {
  const [activeTab, setActiveTab] = useState("ONGOING");
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState(defaultFilters);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const pageSize = 10;

  useEffect(() => {
    loadData(0);
  }, []);

  const loadData = async (page = 0, tab = activeTab) => {
    setIsLoading(true);
    const result = tab === "ONGOING"
      ? await getNegoRequests(page, pageSize)
      : await getNegoRequestLogs(page, pageSize);

    if (result.success && result.data) {
      setList(result.data.content || []);
      setCurrentPage(result.data.number || 0);
      setTotalPages(result.data.totalPages || 0);
      setTotalElements(result.data.totalElements || 0);
    } else {
      setList([]);
      setTotalPages(0);
      setTotalElements(0);
      if (result.error) alert(result.error);
    }
    setIsLoading(false);
  };

  const loadSearchData = async (page = 0, tab = activeTab, f = appliedFilters) => {
    setIsLoading(true);
    const result = tab === "ONGOING"
      ? await searchNegoRequests(f, page, pageSize)
      : await searchNegoRequestLogs(f, page, pageSize);

    if (result.success && result.data) {
      setList(result.data.content || []);
      setCurrentPage(result.data.number || 0);
      setTotalPages(result.data.totalPages || 0);
      setTotalElements(result.data.totalElements || 0);
    } else {
      setList([]);
      setTotalPages(0);
      setTotalElements(0);
      if (result.error) alert(result.error);
    }
    setIsLoading(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setFilters(defaultFilters);
    setIsSearchMode(false);
    setAppliedFilters(null);
    loadData(0, tab);
  };

  const handlePageChange = (page) => {
    if (isSearchMode) {
      loadSearchData(page, activeTab, appliedFilters);
    } else {
      loadData(page, activeTab);
    }
  };

  const handleSearch = () => {
    const f = { ...filters };
    setAppliedFilters(f);
    setIsSearchMode(true);
    setCurrentPage(0);
    loadSearchData(0, activeTab, f);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setIsSearchMode(false);
    setAppliedFilters(null);
    setCurrentPage(0);
    loadData(0, activeTab);
  };

  const renderRequestStatus = (value) => {
    const s = value
      ? { label: "요청 중", className: "adminnegorequest-status-pending" }
      : { label: "완료", className: "adminnegorequest-status-accepted" };
    return <span className={s.className}>{s.label}</span>;
  };

  const renderLogStatus = (value) => {
    const s = STATUS_LABEL[value] || { label: value, className: "" };
    return <span className={s.className}>{s.label}</span>;
  };

  return (
    <div className="adminnegorequest-container">
      <div className="adminnegorequest-wrap">
        <div className="adminnegorequest-tab-navigation">
          <button
            className={`adminnegorequest-tab-btn ${activeTab === "ONGOING" ? "adminnegorequest-tab-active" : ""}`}
            onClick={() => handleTabChange("ONGOING")}
          >
            네고 요청 진행 목록
          </button>
          <button
            className={`adminnegorequest-tab-btn ${activeTab === "LOG" ? "adminnegorequest-tab-active" : ""}`}
            onClick={() => handleTabChange("LOG")}
          >
            네고 요청 내역 목록
          </button>
        </div>

        <div className="adminnegorequest-filter-wrap">
          <div className="adminnegorequest-filter-row">
            <div className="adminnegorequest-filter-group">
              <label>요청일</label>
              <input
                type="date"
                className="adminnegorequest-date-input"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
              <span>~</span>
              <input
                type="date"
                className="adminnegorequest-date-input"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
            {activeTab === "LOG" && (
              <div className="adminnegorequest-filter-group">
                <label>상태</label>
                <select
                  className="adminnegorequest-select-type"
                  value={filters.requestLogStatus}
                  onChange={(e) => setFilters({ ...filters, requestLogStatus: e.target.value })}
                >
                  <option value="">전체</option>
                  <option value="APPROVED">승인</option>
                  <option value="REJECTED">거절</option>
                  <option value="CANCELED">취소</option>
                </select>
              </div>
            )}
          </div>
          <div className="adminnegorequest-filter-row adminnegorequest-search-row">
            <div className="adminnegorequest-search-group">
              <select
                className="adminnegorequest-select-type"
                value={filters.searchType}
                onChange={(e) => setFilters({ ...filters, searchType: e.target.value })}
              >
                <option value="productNm">상품명</option>
                <option value="storeNm">가맹점명</option>
                <option value="custNm">사용자명</option>
              </select>
              <input
                type="text"
                className="adminnegorequest-input"
                placeholder="검색어를 입력해주세요."
                value={filters.searchTerm}
                onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <button className="adminnegorequest-btn-search" onClick={handleSearch}>검색</button>
              <button className="adminnegorequest-btn-reset" onClick={handleReset}>초기화</button>
            </div>
          </div>
        </div>

        <div className="adminnegorequest-table-header">
          <div className="adminnegorequest-table-info">
            총 <span className="adminnegorequest-count">{totalElements}</span>건
          </div>
        </div>

        <table className="adminnegorequest-table">
          <thead>
            <tr>
              <th>상품코드</th>
              <th>상품명</th>
              <th>가맹코드</th>
              <th>가맹점명</th>
              <th>사용자코드</th>
              <th>사용자명</th>
              {activeTab === "ONGOING" ? (
                <>
                  <th>요청가</th>
                  <th>상품가</th>
                  <th>상태</th>
                  <th>요청일</th>
                </>
              ) : (
                <>
                  <th>요청가</th>
                  <th>상태</th>
                  <th>요청일</th>
                  <th>응답일</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="10" className="adminnegorequest-empty">로딩 중...</td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan="10" className="adminnegorequest-empty">데이터가 없습니다.</td>
              </tr>
            ) : activeTab === "ONGOING" ? (
              list.map((item, i) => (
                <tr key={i}>
                  <td>{item.productCode}</td>
                  <td>{item.productNm}</td>
                  <td>{item.storeCode}</td>
                  <td>{item.storeNm}</td>
                  <td>{item.custCode}</td>
                  <td>{item.custNm}</td>
                  <td>{item.requestPrice?.toLocaleString()}원</td>
                  <td>{item.productPrice?.toLocaleString()}원</td>
                  <td>{renderRequestStatus(item.requestStatus)}</td>
                  <td>{formatDate(item.requestDateTime)}</td>
                </tr>
              ))
            ) : (
              list.map((item, i) => (
                <tr key={i}>
                  <td>{item.productCode}</td>
                  <td>{item.productNm}</td>
                  <td>{item.storeCode}</td>
                  <td>{item.storeNm}</td>
                  <td>{item.custCode}</td>
                  <td>{item.custNm}</td>
                  <td>{item.requestPrice?.toLocaleString()}원</td>
                  <td>{renderLogStatus(item.requestLogStatus)}</td>
                  <td>{formatDate(item.requestDateTime)}</td>
                  <td>{formatDate(item.responseDateTime)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default AdminNegoRequest;
